import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLine } from "../components/Motion";
import { scrollToId } from "../lib/scroll";

const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 18 });
  const sy = useSpring(my, { stiffness: 50, damping: 18 });
  const px = useTransform(sx, [-0.5, 0.5], [-14, 14]);
  const py = useTransform(sy, [-0.5, 0.5], [-8, 8]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={onMove}
      className="relative flex min-h-screen items-center overflow-hidden pt-32 pb-20"
    >
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-purple-600/15 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[400px] w-[500px] rounded-full bg-green-500/10 blur-[120px]" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div>
          <MaskedLine delay={0.3}>
            <span className="inline-flex items-center gap-2 rounded-full border border-green-400/30 bg-green-400/5 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.2em] text-green-300">
              <span className="h-1.5 w-1.5 rounded-full bg-green-400 pulse-glow" />
              Shelf-life advisory for food businesses
            </span>
          </MaskedLine>
          <h1 className="mt-8 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-slate-50 sm:text-5xl lg:text-[4.2rem]">
            <MaskedLine delay={0.45}>Science-backed</MaskedLine>
            <MaskedLine delay={0.58}>shelf-life estimates,</MaskedLine>
            <MaskedLine delay={0.71}>
              <span className="text-gradient">before the lab bill.</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.9}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Shelf Sense helps food brands make shelf-life and date-marking decisions, grounded in established food science and the Food Standards Code, before committing to expensive laboratory testing.
            </p>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); scrollToId("contact"); }}
              data-testid="hero-cta-contact"
              className="group inline-flex items-center gap-2 rounded-full bg-purple-600 px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-purple-950/50 transition-all duration-300 hover:bg-green-500 hover:shadow-green-950/50"
            >
              Get in Touch
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#problem"
              onClick={(e) => { e.preventDefault(); scrollToId("problem"); }}
              data-testid="hero-cta-problem"
              className="group inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-base font-semibold text-slate-200 transition-colors duration-300 hover:border-green-400/50 hover:text-green-300"
            >
              See the problem
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
          </motion.div>
        </div>

        <motion.div style={{ x: px, y: py }} className="relative mx-auto w-full max-w-lg">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="spotlight-frame overflow-hidden rounded-[2rem]" data-testid="hero-image-panel">
              <img
                src="https://images.unsplash.com/photo-1566216867955-5b2e8c77e45f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxmb29kJTIwc2NpZW5jZSUyMGxhYm9yYXRvcnklMjByZXNlYXJjaHxlbnwwfHx8fDE3OTEwMjY4MTV8MA&ixlib=rb-4.1.0&q=85"
                alt="Microbiological sampling and food formulation review"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
            <div className="mt-5 grid grid-cols-2 gap-4">
              <div className="glass rounded-2xl p-5" data-testid="hero-stat-lab">
                <p className="font-display text-lg font-bold text-slate-50">Thousands</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">often spent per product on a lab shelf-life study</p>
              </div>
              <div className="glass rounded-2xl p-5" data-testid="hero-stat-speed">
                <p className="font-display text-lg font-bold text-green-400">Fast turnaround</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">for a science-backed estimate</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
